import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, Archive, FileSpreadsheet, FileText, Filter, RefreshCw,
  Briefcase, ListChecks, HardHat, GraduationCap, CalendarClock, FileType2,
} from "lucide-react";
import { toast } from "sonner";
import { diffLines, diffWords } from "diff";
import { apiRequest, downloadFile, API_BASE } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DOCUMENT_TYPE_LABELS } from "./Exports.constants";
import CompanyPicker from "./Exports.CompanyPicker";
import BulkCard from "./Exports.BulkCard";
import GeneratorsGrid from "./Exports.GeneratorsGrid";
import ExportsStats from "./Exports.Stats";
import FiltersPanel from "./Exports.FiltersPanel";
import DocumentRow from "./Exports.DocumentRow";
import PreviewDialog from "./Exports.PreviewDialog";
import DiffDialog from "./Exports.DiffDialog";
import SignDialog from "./Exports.SignDialog";

export default function Exports() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"].includes(user.role);
  const [company, setCompany] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(""); // current action key while generating

  // Advanced filters
  const [signerFilter, setSignerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [shaPrefixFilter, setShaPrefixFilter] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Preview modal state
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const previewContainerRef = useRef(null);

  // Sign modal state
  const [signDoc, setSignDoc] = useState(null);
  const [signForm, setSignForm] = useState({ signer_name: "", signer_fiscal_code: "", qtsp_provider: "" });
  const [signFile, setSignFile] = useState(null);
  const [signBusy, setSignBusy] = useState(false);

  // Diff modal state
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLeftId, setDiffLeftId] = useState("");
  const [diffRightId, setDiffRightId] = useState("");
  const [diffLeftText, setDiffLeftText] = useState("");
  const [diffRightText, setDiffRightText] = useState("");
  const [diffBusy, setDiffBusy] = useState(false);
  const [diffError, setDiffError] = useState("");
  const [diffMode, setDiffMode] = useState("lines"); // "lines" | "words"
  const [diffExportBusy, setDiffExportBusy] = useState("");

  // Diff comments (collaboration)
  const [diffComments, setDiffComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState({});
  const [commentBusy, setCommentBusy] = useState(false);

  // Mentions autocomplete
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [mentionAnchor, setMentionAnchor] = useState(null);

  // Filter presets
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [presetBusy, setPresetBusy] = useState(false);

  const load = async () => {
    if (!id) return;
    // Build query string for backend filters
    const params = new URLSearchParams();
    if (typeFilter) params.set("document_type", typeFilter);
    if (statusFilter) params.set("status", statusFilter);
    if (signerFilter.trim()) params.set("signer", signerFilter.trim());
    if (shaPrefixFilter.trim() && shaPrefixFilter.trim().length >= 4) params.set("sha_prefix", shaPrefixFilter.trim());
    if (fromDateFilter) params.set("from_date", fromDateFilter);
    if (toDateFilter) params.set("to_date", toDateFilter);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const [companyData, taskData, docsData] = await Promise.all([
      apiRequest(`/companies/${id}`),
      apiRequest(`/companies/${id}/job-tasks`).catch(() => []),
      apiRequest(`/companies/${id}/generated-documents${qs}`),
    ]);
    setCompany(companyData);
    setTasks(taskData || []);
    setDocuments(docsData || []);
    if (taskData?.length && !selectedTaskId) setSelectedTaskId(taskData[0].id);
  };

  useEffect(() => { load().catch((err) => setError(err.message)); }, [id, typeFilter, statusFilter, signerFilter, shaPrefixFilter, fromDateFilter, toDateFilter]);

  // Load filter presets (always called — hook rules)
  useEffect(() => {
    if (!id) return;
    apiRequest("/me/filter-presets?scope=generated_documents")
      .then((list) => setPresets(list || []))
      .catch(() => {});
  }, [id]);

  // Load diff comments when diff modal opens
  useEffect(() => {
    const diffPair = (diffLeftId && diffRightId) ? [diffLeftId, diffRightId].sort().join("__") : "";
    if (!id || !diffOpen || !diffPair) {
      setDiffComments([]);
      return;
    }
    apiRequest(`/companies/${id}/diff-comments?diff_pair_id=${diffPair}`)
      .then((list) => setDiffComments(list || []))
      .catch((err) => setDiffError(err.message));
  }, [id, diffOpen, diffLeftId, diffRightId]);

  // Cleanup blob URL when modal closes
  useEffect(() => {
    if (!previewDoc && previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl("");
    }
  }, [previewDoc, previewBlobUrl]);

  // Lazy-render DOCX preview when modal opens with a docx doc
  useEffect(() => {
    let cancelled = false;
    const renderDocxPreview = async () => {
      if (!previewDoc) return;
      const isPdf = (previewDoc.mime_type || "").includes("pdf") || previewDoc.file_name?.toLowerCase().endsWith(".pdf");
      if (isPdf) return; // PDFs use iframe directly
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const token = localStorage.getItem("dvr_token");
        const res = await fetch(`${API_BASE}/companies/${id}/generated-documents/${previewDoc.id}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Anteprima non disponibile");
        const blob = await res.blob();
        if (cancelled) return;
        const docxPreview = await import("docx-preview");
        const container = previewContainerRef.current;
        if (!container) return;
        container.innerHTML = "";
        await docxPreview.renderAsync(blob, container, null, {
          className: "docx-preview", inWrapper: true, ignoreWidth: false,
          ignoreHeight: false, defaultFont: { family: "Arial", size: 11 },
        });
      } catch (err) {
        if (!cancelled) setPreviewError(err.message || "Errore anteprima DOCX");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    renderDocxPreview();
    return () => { cancelled = true; };
  }, [previewDoc, id]);

  // Real-time updates via WebSocket: refresh comments when peers add/edit/delete
  useEffect(() => {
    const handler = (ev) => {
      const data = ev.detail || {};
      if (!diffOpen || !id) return;
      const diffPair = (diffLeftId && diffRightId) ? [diffLeftId, diffRightId].sort().join("__") : "";
      if (data.company_id === id && data.diff_pair_id === diffPair) {
        apiRequest(`/companies/${id}/diff-comments?diff_pair_id=${diffPair}`)
          .then((list) => setDiffComments(list || []))
          .catch(() => {});
      }
    };
    const mentionHandler = (ev) => {
      const data = ev.detail || {};
      toast.info(`${data.author_name} ti ha menzionato`, {
        description: data.text?.slice(0, 120),
        duration: 8000,
      });
    };
    window.addEventListener("dvr-diff_comment", handler);
    window.addEventListener("dvr-diff_comment_mention", mentionHandler);
    return () => {
      window.removeEventListener("dvr-diff_comment", handler);
      window.removeEventListener("dvr-diff_comment_mention", mentionHandler);
    };
  }, [id, diffOpen, diffLeftId, diffRightId]);

  if (!id) return <CompanyPicker />;
  if (!company) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="exports-loading">Caricamento esportazioni...</div>;

  const diffPairId = (diffLeftId && diffRightId)
    ? [diffLeftId, diffRightId].sort().join("__")
    : "";

  // Filtering is now server-side (see load()).
  const filtered = documents;

  const generate = async (kind, opts = {}) => {
    setError("");
    setBusy(kind + (opts.taskId ? `:${opts.taskId}` : ""));
    try {
      const path = opts.taskId
        ? `/companies/${id}/exports/task-extract/${opts.taskId}`
        : `/companies/${id}/exports/${kind.replace("_", "-")}`;
      const res = await apiRequest(path, { method: "POST" });
      toast.success(`Documento generato`, { description: `${DOCUMENT_TYPE_LABELS[res.document_type] || res.document_type} v${res.version_number}` });
      await load();
    } catch (err) {
      setError(err.message);
      toast.error("Generazione non riuscita", { description: err.message });
    } finally {
      setBusy("");
    }
  };

  const batchGenerate = async () => {
    setError("");
    setBusy("batch");
    try {
      const body = {
        types: ["dvr_pdf", "dvr_docx", "improvement_plan", "ppe_register", "training_plan", "deadlines"],
      };
      const res = await apiRequest(`/companies/${id}/exports/batch`, {
        method: "POST", body: JSON.stringify(body),
      });
      toast.success(`Batch completato`, { description: `${res.length} documenti generati` });
      await load();
    } catch (err) {
      setError(err.message);
      toast.error("Batch fallito", { description: err.message });
    } finally {
      setBusy("");
    }
  };

  const downloadZip = async (filterType) => {
    setError("");
    const qs = filterType ? `?document_type=${filterType}` : "";
    try {
      await downloadFile(
        `/companies/${id}/generated-documents/download-zip${qs}`,
        `DVR_export_${id}.zip`,
      );
      toast.success("ZIP scaricato");
    } catch (err) {
      setError(err.message);
      toast.error("Download ZIP fallito", { description: err.message });
    }
  };

  const downloadRegistry = async (format) => {
    setError("");
    try {
      await downloadFile(
        `/companies/${id}/generated-documents/export-${format}`,
        `registro_${id}.${format}`,
      );
      toast.success(`Registro ${format.toUpperCase()} scaricato`);
    } catch (err) {
      setError(err.message);
      toast.error(`Export ${format.toUpperCase()} fallito`, { description: err.message });
    }
  };

  const download = async (doc) => {
    setError("");
    try {
      await downloadFile(`/companies/${id}/generated-documents/${doc.id}/download`, doc.file_name);
      toast.success("Download avviato");
    } catch (err) { setError(err.message); toast.error("Download fallito", { description: err.message }); }
  };

  const openPreview = (doc) => {
    setPreviewError("");
    setPreviewDoc(doc);
    const isPdf = (doc.mime_type || "").includes("pdf") || doc.file_name?.toLowerCase().endsWith(".pdf");
    if (isPdf) {
      const token = localStorage.getItem("dvr_token");
      (async () => {
        setPreviewLoading(true);
        try {
          const res = await fetch(`${API_BASE}/companies/${id}/generated-documents/${doc.id}/preview`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error("Anteprima PDF non disponibile");
          const blob = await res.blob();
          setPreviewBlobUrl(URL.createObjectURL(blob));
        } catch (err) {
          setPreviewError(err.message);
        } finally {
          setPreviewLoading(false);
        }
      })();
    }
  };

  const updateStatus = async (doc, status) => {
    setError("");
    try {
      const res = await apiRequest(`/companies/${id}/generated-documents/${doc.id}`, {
        method: "PUT", body: JSON.stringify({ status }),
      });
      if (status === "finale") {
        const promoted = res.metadata?.promoted_company_document_id;
        const email = res.metadata?.finale_email;
        toast.success("Documento promosso a 'finale'", {
          description: [
            promoted ? "Copia archiviata nel modulo Documenti" : null,
            email?.sent ? `Email inviata a ${email.recipients?.join(", ")}` :
              email?.reason === "smtp_non_configurato" ? "SMTP non configurato: email saltata" : null,
          ].filter(Boolean).join(" · "),
        });
      } else {
        toast.success(`Stato aggiornato: ${status}`);
      }
      await load();
    } catch (err) { setError(err.message); toast.error("Aggiornamento fallito", { description: err.message }); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Eliminare definitivamente ${doc.file_name}?`)) return;
    setError("");
    try {
      await apiRequest(`/companies/${id}/generated-documents/${doc.id}`, { method: "DELETE" });
      toast.success("Documento eliminato");
      await load();
    } catch (err) { setError(err.message); toast.error("Eliminazione fallita", { description: err.message }); }
  };

  const submitSign = async (event) => {
    event.preventDefault();
    if (!signFile || !signDoc) return;
    setSignBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("signer_name", signForm.signer_name);
      if (signForm.signer_fiscal_code) fd.append("signer_fiscal_code", signForm.signer_fiscal_code);
      if (signForm.qtsp_provider) fd.append("qtsp_provider", signForm.qtsp_provider);
      fd.append("signed_file", signFile);
      const res = await apiRequest(
        `/companies/${id}/generated-documents/${signDoc.id}/eidas-sign`,
        { method: "POST", body: fd },
      );
      toast.success("Firma eIDAS registrata", {
        description: `Tipo firma: ${res.metadata?.signature_kind || "—"} · stato: ${res.status}`,
      });
      setSignDoc(null);
      setSignFile(null);
      setSignForm({ signer_name: "", signer_fiscal_code: "", qtsp_provider: "" });
      await load();
    } catch (err) {
      setError(err.message);
      toast.error("Firma fallita", { description: err.message });
    } finally {
      setSignBusy(false);
    }
  };

  const openDiff = (doc) => {
    // Preselect: doc on the right, latest "bozza" of same type on the left
    const sameType = documents.filter((d) => d.document_type === doc.document_type && d.id !== doc.id);
    const draft = sameType.find((d) => d.status === "bozza") || sameType[0] || null;
    setDiffLeftId(draft?.id || "");
    setDiffRightId(doc.id);
    setDiffLeftText("");
    setDiffRightText("");
    setDiffError("");
    setDiffOpen(true);
  };

  const fetchDiffTexts = async () => {
    if (!diffLeftId || !diffRightId) { setDiffError("Seleziona entrambe le versioni"); return; }
    if (diffLeftId === diffRightId) { setDiffError("Seleziona due documenti diversi"); return; }
    setDiffBusy(true);
    setDiffError("");
    try {
      const [left, right] = await Promise.all([
        apiRequest(`/companies/${id}/generated-documents/${diffLeftId}/text`),
        apiRequest(`/companies/${id}/generated-documents/${diffRightId}/text`),
      ]);
      setDiffLeftText(left.text || "");
      setDiffRightText(right.text || "");
    } catch (err) {
      setDiffError(err.message);
      toast.error("Estrazione testo fallita", { description: err.message });
    } finally {
      setDiffBusy(false);
    }
  };

  const handleCommentTyping = async (key, value) => {
    setCommentDraft({ ...commentDraft, [key]: value });
    // Detect @mention being typed: pattern @[word]$
    const match = value.match(/@([A-Za-z0-9_.-]{1,40})$/);
    if (match) {
      try {
        const list = await apiRequest(`/companies/${id}/diff-comments/mention-candidates?q=${encodeURIComponent(match[1])}`);
        setMentionCandidates(list || []);
        setMentionAnchor({ key, query: match[1] });
      } catch { /* silent */ }
    } else if (mentionAnchor && mentionAnchor.key === key) {
      setMentionAnchor(null);
    }
  };

  const insertMention = (candidate) => {
    if (!mentionAnchor) return;
    const k = mentionAnchor.key;
    const current = commentDraft[k] || "";
    const replaced = current.replace(/@[A-Za-z0-9_.-]{1,40}$/, `@${candidate.mention_handle} `);
    setCommentDraft({ ...commentDraft, [k]: replaced });
    setMentionAnchor(null);
    setMentionCandidates([]);
  };

  const loadDiffComments = async () => {
    const diffPair = (diffLeftId && diffRightId) ? [diffLeftId, diffRightId].sort().join("__") : "";
    if (!diffPair) return;
    try {
      const list = await apiRequest(`/companies/${id}/diff-comments?diff_pair_id=${diffPair}`);
      setDiffComments(list || []);
    } catch (err) {
      setDiffError(err.message);
    }
  };

  const submitComment = async (side, line_index) => {
    const text = (commentDraft[`${side}:${line_index}`] || "").trim();
    if (!text || !diffPairId) return;
    setCommentBusy(true);
    try {
      await apiRequest(`/companies/${id}/diff-comments`, {
        method: "POST",
        body: JSON.stringify({ diff_pair_id: diffPairId, side, line_index, text }),
      });
      setCommentDraft({ ...commentDraft, [`${side}:${line_index}`]: "" });
      toast.success("Commento aggiunto");
      await loadDiffComments();
    } catch (err) {
      toast.error("Commento non aggiunto", { description: err.message });
    } finally {
      setCommentBusy(false);
    }
  };

  const toggleResolved = async (comment) => {
    try {
      await apiRequest(`/companies/${id}/diff-comments/${comment.id}`, {
        method: "PUT", body: JSON.stringify({ resolved: !comment.resolved }),
      });
      toast.success(comment.resolved ? "Riaperto" : "Risolto");
      await loadDiffComments();
    } catch (err) { toast.error("Operazione fallita", { description: err.message }); }
  };

  const deleteComment = async (comment) => {
    if (!window.confirm("Eliminare il commento?")) return;
    try {
      await apiRequest(`/companies/${id}/diff-comments/${comment.id}`, { method: "DELETE" });
      toast.success("Commento eliminato");
      await loadDiffComments();
    } catch (err) { toast.error("Eliminazione fallita", { description: err.message }); }
  };

  const exportDiff = async (format) => {
    if (!diffLeftId || !diffRightId || diffLeftId === diffRightId) return;
    setDiffExportBusy(format);
    try {
      const path = format === "pdf-visual"
        ? `/companies/${id}/exports/diff-visual`
        : `/companies/${id}/exports/diff`;
      const body = format === "pdf-visual"
        ? { left_id: diffLeftId, right_id: diffRightId }
        : { left_id: diffLeftId, right_id: diffRightId, format };
      const res = await apiRequest(path, { method: "POST", body: JSON.stringify(body) });
      const meta = res.metadata || {};
      toast.success(`Report ${format.toUpperCase()} generato`, {
        description: format === "pdf-visual"
          ? `Pagine confrontate: ${meta.pages_compared} · con differenze: ${meta.pages_with_diff}`
          : `${meta.blocks || 0} blocchi · +${meta.added_lines || 0}/−${meta.removed_lines || 0}`,
      });
      await load();
    } catch (err) {
      toast.error("Export diff fallito", { description: err.message });
    } finally {
      setDiffExportBusy("");
    }
  };

  const exportPresets = async () => {
    try {
      await downloadFile(
        "/me/filter-presets/export?scope=generated_documents",
        "filter_presets_generated_documents.json",
      );
      toast.success("Preset esportati");
    } catch (err) { toast.error("Export preset fallito", { description: err.message }); }
  };

  const importPresets = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset input
    if (!file) return;
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const res = await apiRequest("/me/filter-presets/import?overwrite=false", {
        method: "POST", body: JSON.stringify(bundle),
      });
      toast.success("Preset importati", {
        description: `${res.imported} importati · ${res.skipped} saltati · ${res.updated} aggiornati`,
      });
      const list = await apiRequest("/me/filter-presets?scope=generated_documents");
      setPresets(list || []);
    } catch (err) {
      toast.error("Import preset fallito", { description: err.message });
    }
  };

  const computedDiff = (() => {
    if (!diffLeftText || !diffRightText) return null;
    const fn = diffMode === "words" ? diffWords : diffLines;
    const parts = fn(diffLeftText, diffRightText, { ignoreWhitespace: false });
    const added = parts.filter((p) => p.added).reduce((acc, p) => acc + (p.count || 1), 0);
    const removed = parts.filter((p) => p.removed).reduce((acc, p) => acc + (p.count || 1), 0);
    return { parts, added, removed };
  })();

  const clearFilters = () => {
    setTypeFilter(""); setStatusFilter(""); setSignerFilter("");
    setShaPrefixFilter(""); setFromDateFilter(""); setToDateFilter("");
  };

  const loadPresets = async () => {
    try {
      const list = await apiRequest("/me/filter-presets?scope=generated_documents");
      setPresets(list || []);
    } catch (err) { /* silent */ }
  };

  const applyPreset = (preset) => {
    const f = preset.filters || {};
    setTypeFilter(f.document_type || "");
    setStatusFilter(f.status || "");
    setSignerFilter(f.signer || "");
    setShaPrefixFilter(f.sha_prefix || "");
    setFromDateFilter(f.from_date || "");
    setToDateFilter(f.to_date || "");
    toast.success(`Preset applicato: ${preset.name}`);
  };

  const saveCurrentAsPreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Nome preset obbligatorio"); return; }
    setPresetBusy(true);
    try {
      await apiRequest("/me/filter-presets", {
        method: "POST",
        body: JSON.stringify({
          name, scope: "generated_documents",
          filters: {
            document_type: typeFilter || undefined,
            status: statusFilter || undefined,
            signer: signerFilter || undefined,
            sha_prefix: shaPrefixFilter || undefined,
            from_date: fromDateFilter || undefined,
            to_date: toDateFilter || undefined,
          },
        }),
      });
      toast.success(`Preset salvato: ${name}`);
      setPresetName("");
      await loadPresets();
    } catch (err) {
      toast.error("Salvataggio preset fallito", { description: err.message });
    } finally {
      setPresetBusy(false);
    }
  };

  const deletePreset = async (preset) => {
    if (!window.confirm(`Eliminare preset "${preset.name}"?`)) return;
    try {
      await apiRequest(`/me/filter-presets/${preset.id}`, { method: "DELETE" });
      toast.success("Preset eliminato");
      await loadPresets();
    } catch (err) {
      toast.error("Eliminazione preset fallita", { description: err.message });
    }
  };

  const generatorCards = [
    { kind: "dvr-pdf", key: "dvr_pdf", icon: FileText, color: "text-blue-900", description: "DVR completo formato PDF con header azienda, firme, revisioni e watermark." },
    { kind: "dvr-docx", key: "dvr_docx", icon: FileType2, color: "text-indigo-700", description: "DVR modificabile in formato Word con tutte le sezioni." },
    { kind: "improvement-plan", key: "improvement_plan", icon: ListChecks, color: "text-emerald-700", description: "Piano di miglioramento con azioni, priorità, scadenze e stato." },
    { kind: "ppe-register", key: "ppe_register", icon: HardHat, color: "text-orange-700", description: "Registro DPI per lavoratore, categoria e scadenze." },
    { kind: "training-plan", key: "training_plan", icon: GraduationCap, color: "text-purple-700", description: "Piano di formazione con corsi, scadenze e completati." },
    { kind: "deadlines", key: "deadlines", icon: CalendarClock, color: "text-amber-700", description: "Scadenzario aggregato (formazione, DPI, sanitaria, documenti, azioni)." },
  ];

  const isPreviewPdf = previewDoc && ((previewDoc.mime_type || "").includes("pdf") || previewDoc.file_name?.toLowerCase().endsWith(".pdf"));
  const isPreviewDocx = previewDoc && !isPreviewPdf && (previewDoc.file_name?.toLowerCase().endsWith(".docx") || (previewDoc.mime_type || "").includes("wordprocessing"));

  return (
    <div className="space-y-6" data-testid="exports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="exports-back-button"><ArrowLeft className="h-4 w-4" /> Scheda azienda</Button>
        <Badge className="bg-blue-50 text-blue-900" data-testid="exports-company-badge">{company.business_name}</Badge>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Esportazioni e generazioni</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Genera documenti DVR</h2>
        <p className="mt-2 text-slate-600">Ogni documento riporta intestazione azienda, titolo, data, numero revisione, paginazione, tabella firme, registro revisioni e watermark <strong>BOZZA</strong> finché il DVR non è firmato (<strong>VALIDATO</strong> dopo la firma). Al passaggio in stato <strong>finale</strong> il documento viene promosso automaticamente nel modulo Documenti dell&apos;azienda e (se SMTP attivo) viene inviata notifica al datore di lavoro.</p>
      </section>

      {error && <Alert variant="destructive" data-testid="exports-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      {canWrite && <BulkCard busy={busy} batchGenerate={batchGenerate} downloadZip={downloadZip} />}

      {canWrite && (
        <GeneratorsGrid
          busy={busy}
          generate={generate}
          tasks={tasks}
          selectedTaskId={selectedTaskId}
          setSelectedTaskId={setSelectedTaskId}
          generatorCards={generatorCards}
        />
      )}

      <ExportsStats documents={documents} />

      <Card className="shadow-none" data-testid="exports-list-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="font-heading">Documenti generati</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant={showAdvancedFilters ? "default" : "outline"} size="sm" onClick={() => setShowAdvancedFilters((v) => !v)} className={showAdvancedFilters ? "bg-blue-900" : ""} data-testid="toggle-filters-button"><Filter className="h-4 w-4" /> Filtri</Button>
              <Button variant="outline" size="sm" onClick={() => downloadRegistry("csv")} data-testid="export-registry-csv-button"><FileText className="h-4 w-4" /> CSV</Button>
              <Button variant="outline" size="sm" onClick={() => downloadRegistry("xlsx")} data-testid="export-registry-xlsx-button"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
              {typeFilter && <Button variant="outline" size="sm" onClick={() => downloadZip(typeFilter)} data-testid="download-zip-filtered-button"><Archive className="h-4 w-4" /> ZIP filtrati</Button>}
              <Button variant="outline" size="sm" onClick={() => load().catch((e) => setError(e.message))} data-testid="exports-refresh-button"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
            </div>
          </div>
          {showAdvancedFilters && (
            <FiltersPanel
              typeFilter={typeFilter} setTypeFilter={setTypeFilter}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              signerFilter={signerFilter} setSignerFilter={setSignerFilter}
              shaPrefixFilter={shaPrefixFilter} setShaPrefixFilter={setShaPrefixFilter}
              fromDateFilter={fromDateFilter} setFromDateFilter={setFromDateFilter}
              toDateFilter={toDateFilter} setToDateFilter={setToDateFilter}
              filteredCount={filtered.length} clearFilters={clearFilters}
              presets={presets} presetName={presetName} setPresetName={setPresetName}
              presetBusy={presetBusy}
              applyPreset={applyPreset} deletePreset={deletePreset}
              saveCurrentAsPreset={saveCurrentAsPreset}
              exportPresets={exportPresets} importPresets={importPresets}
            />
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {filtered.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="exports-empty">Nessun documento generato. Usa i pulsanti qui sopra per crearli.</p>}
          {filtered.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              canWrite={canWrite}
              openPreview={openPreview}
              openDiff={openDiff}
              download={download}
              setSignDoc={setSignDoc}
              updateStatus={updateStatus}
              remove={remove}
            />
          ))}
        </CardContent>
      </Card>

      <PreviewDialog
        previewDoc={previewDoc} setPreviewDoc={setPreviewDoc}
        previewError={previewError} previewLoading={previewLoading}
        previewBlobUrl={previewBlobUrl} previewContainerRef={previewContainerRef}
        isPreviewPdf={isPreviewPdf} isPreviewDocx={isPreviewDocx}
      />

      <DiffDialog
        diffOpen={diffOpen} setDiffOpen={setDiffOpen}
        documents={documents}
        diffLeftId={diffLeftId} setDiffLeftId={setDiffLeftId}
        diffRightId={diffRightId} setDiffRightId={setDiffRightId}
        diffLeftText={diffLeftText} setDiffLeftText={setDiffLeftText}
        diffRightText={diffRightText} setDiffRightText={setDiffRightText}
        diffBusy={diffBusy} diffError={diffError}
        diffMode={diffMode} setDiffMode={setDiffMode}
        diffExportBusy={diffExportBusy} fetchDiffTexts={fetchDiffTexts}
        exportDiff={exportDiff}
        computedDiff={computedDiff} diffComments={diffComments} canWrite={canWrite}
        commentDraft={commentDraft} setCommentDraft={setCommentDraft}
        commentBusy={commentBusy}
        handleCommentTyping={handleCommentTyping} submitComment={submitComment}
        toggleResolved={toggleResolved} deleteComment={deleteComment}
        insertMention={insertMention}
        mentionAnchor={mentionAnchor} setMentionAnchor={setMentionAnchor}
        mentionCandidates={mentionCandidates}
      />

      <SignDialog
        signDoc={signDoc} setSignDoc={setSignDoc}
        signForm={signForm} setSignForm={setSignForm}
        signFile={signFile} setSignFile={setSignFile}
        signBusy={signBusy} submitSign={submitSign}
      />
    </div>
  );
}
